import { observable } from "mobx";
import { plainToClass } from "class-transformer";
import { TravelRequestFormData } from "./useTravelRequestForm";
import {
  TransportationType,
  AccommodationTypeOf,
  RideType,
  TravelRequestTime,
  LuggageType,
  TicketDifficultyLevel
} from "@bonliva-crm/shared";
import locale from "@bonliva-crm/locale";
import { createObjectID } from "@bonliva-crm/common";
import { isEmpty } from "lodash";

export class TravelRequest {
  static InsertRideAfter(current: TravelRequest, index: number) {
    const source = current.rides[index];

    if (!source) {
      return;
    }

    const item = new TravelRequestReturnRide();
    item.dir1.clone(source.dir1);

    if (source.dir2 && item.dir2) {
      item.dir2.clone(source.dir2);
    }

    item.dir1.date = null;
    item.dir2.date = null;

    item.rideType = source.rideType;
    item.transportationType = source.transportationType;

    current.rides.splice(index + 1, 0, item);
  }

  static Create(data?: TravelRequestFormData, travelPreferences?: string) {
    const item = data
      ? plainToClass(TravelRequest, data, {
          excludePrefixes: ["_"]
        })
      : new TravelRequest();

    if (data?._id) {
      item.id = data._id;
    }

    if (travelPreferences) {
      item.travelPreferences = travelPreferences;
    }

    return item;
  }

  static ValidateCustom(data: TravelRequest, hasBooking = true) {
    const errors: Record<string, any> = {};

    if (!hasBooking) {
      if (!data.consultantId) {
        errors.consultantId = locale.Consultants;
      }

      if (!data.projectId) {
        errors.projectId = locale.Project;
      }

      if (!data.costCenterId) {
        errors.costCenterId = locale.CostCenter;
      }
    }

    if (!data.travelSuggestion) {
      errors.travelSuggestion = locale.travelRequest.shouldBeBookedDirectly;
    }

    if (data.isCancelable === null) {
      errors.isCancelable = locale.travelRequest.isCancelable;
    }

    if (data.isFlexible === null) {
      errors.isFlexible = locale.travelRequest.isFlexible;
    }

    if (!data.ticketDifficultyLevel) {
      errors.ticketDifficultyLevel = locale.ticketDifficultyLevel.title;
    }

    // Validate luggage requirement for flights
    const isFlight = data.rides.some((ride) =>
      ride.transportationType?.includes?.(TransportationType.Flight)
    );

    if (isFlight && !data.luggageType) {
      errors.luggageType = locale.travelRequest.luggageTypeLabel;
    }

    // Reschedule validation - comment is required when reschedule is checked
    if (data.isReschedule && !data.rescheduleComment?.trim()) {
      errors.rescheduleComment = locale.travelRequest.rescheduleComment;
    }

    // Validate each ride
    data.rides.forEach((ride, i) => {
      const rideErrors = this.validateRide(ride);
      if (Object.keys(rideErrors).length > 0) {
        errors.rides = errors.rides || [];
        errors.rides[i] = rideErrors;
      }
    });

    return errors;
  }

  private static validateRide(ride: TravelRequestReturnRide) {
    const rideErrors: Record<string, any> = {};

    // Validate transportation type
    if (isEmpty(ride.transportationType)) {
      rideErrors.transportationType = locale.travelRequest.transportationType;
    }

    // Validate outbound direction
    const dir1Errors = this.validateDirection(ride.dir1);
    if (Object.keys(dir1Errors).length > 0) {
      rideErrors.dir1 = dir1Errors;
    }

    const dir2Errors = this.validateDirection(
      ride.dir2,
      ride.rideType === RideType.RoundTrip
    );
    if (Object.keys(dir2Errors).length > 0) {
      rideErrors.dir2 = dir2Errors;
    }

    return rideErrors;
  }

  private static validateDirection(
    direction: TravelRequestRide | null,
    validateDate = true
  ) {
    const errors: Record<string, any> = {};

    if (!direction) return errors;

    if (validateDate && !direction.date) {
      errors.date = locale.travelRequest.date;
    }


    if (!direction.city) {
      errors.city = locale.travelRequest.city;
    }

    return errors;
  }

  id = createObjectID();

  @observable
  deadline: Date;

  @observable
  rides: Array<TravelRequestReturnRide> = [new TravelRequestReturnRide()];

  /** @deprecated */
  @observable
  luggage?: string;

  @observable
  luggageType: LuggageType | null = null;

  @observable
  travelPreferences: string = "";

  @observable
  isCancelable: string | null = null;

  @observable
  isFlexible: string | null = null;

  @observable
  ticketDifficultyLevel: TicketDifficultyLevel | null = null;

  @observable
  travelSuggestion: AccommodationTypeOf | null = null;

  @observable
  consultantId: string | null = null;

  @observable
  projectId: string | null = null;

  @observable
  costCenterId: string | null = null;

  @observable
  isReschedule: boolean = false;

  @observable
  rescheduleComment: string = "";
}

export class TravelRequestRide {
  @observable
  city: string = "";

  @observable
  country = "Sweden"; // Default to Sweden

  @observable
  date: Date | null = null;

  @observable
  time: TravelRequestTime | null = null;

  @observable
  comment: string = "";

  clone(ride: TravelRequestRide) {
    this.city = ride.city;
    this.country = ride.country;
    this.time = ride.time;
    this.date = ride.date;
    this.comment = ride.comment;
  }
}

export class TravelRequestReturnRide {
  @observable
  rideType: RideType = RideType.RoundTrip;

  @observable
  transportationType: TransportationType[] = [];

  @observable
  dir1: TravelRequestRide = new TravelRequestRide();

  @observable
  dir2: TravelRequestRide = new TravelRequestRide();
}
